import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload as UploadIcon, Edit2, Trash2, Send, DollarSign, RefreshCw, Download, AlertTriangle, Copy, XCircle } from 'lucide-react';

const API_URL = '/api';

function Upload() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [depositModal, setDepositModal] = useState({ show: false, id: null, nominal: '' });
  
  // Checkbox states
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page, sortBy, order, statusFilter]);

  useEffect(() => {
    // Reset selections when data changes
    setSelectedIds([]);
    setSelectAll(false);
  }, [data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        sortBy,
        order
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await axios.get(`${API_URL}/upload`, { params });
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Pilih file terlebih dahulu');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
	  setLoading(true);
	  const response = await axios.post(`${API_URL}/upload`, formData, {
		headers: { 'Content-Type': 'multipart/form-data' }
	  });
	  alert(`✅ Upload berhasil!\n\n${response.data.count} data ter-upload dan nomor telah dinormalisasi dengan prefix 62.`);
	  setFile(null);
	  fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload gagal: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus data ini?')) return;

    try {
      await axios.delete(`${API_URL}/upload/${id}`);
      alert('Data berhasil dihapus');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Gagal menghapus data');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData(item);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API_URL}/upload/${editingId}`, editData);
      alert('Data berhasil diupdate. Nomor telah dinormalisasi.');
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      alert('Gagal update data');
    }
  };

  const handleMarkBlast = async (id) => {
    if (!window.confirm('Tandai data ini sudah di-blast?\n\nData akan dipindahkan ke menu Tracking dan counter Dashboard akan bertambah.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/upload/mark-blast/${id}`);

      if (response.data.success) {
        alert('✅ Data berhasil di-blast!\n\nSilahkan cek:\n- Dashboard: Counter bertambah\n- Tracking: Data sudah masuk');
        fetchData();
      }
    } catch (error) {
      console.error('Mark blast error:', error);
      alert('❌ Gagal mark blast: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!depositModal.nominal || depositModal.nominal <= 0) {
      alert('Masukkan nominal deposit yang valid');
      return;
    }

    try {
      await axios.post(`${API_URL}/upload/deposit/${depositModal.id}`, {
        nominal_deposit: parseFloat(depositModal.nominal)
      });
      alert('Deposit berhasil dicatat');
      setDepositModal({ show: false, id: null, nominal: '' });
      fetchData();
    } catch (error) {
      console.error('Deposit error:', error);
      alert('Gagal mencatat deposit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCheckAll = async () => {
    if (!window.confirm('Cek semua nomor yang belum dicek? Proses ini mungkin memakan waktu.')) return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/whatsapp/check-all`);
      alert('Pengecekan selesai!');
      fetchData();
    } catch (error) {
      console.error('Check all error:', error);
      alert('Gagal cek nomor: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadValid = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/upload/download-valid`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'data_valid.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ Download berhasil! File berisi data valid dengan nomor ternormalisasi (prefix 62)');
    } catch (error) {
      console.error('Download error:', error);
      alert('❌ ' + (error.response?.data?.error || 'Gagal download data'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvalid = async () => {
    if (!window.confirm('⚠️ PERHATIAN!\n\nYakin ingin menghapus SEMUA data invalid?\nTindakan ini tidak dapat dibatalkan!')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/upload/delete-invalid/all`);
      alert('✅ ' + response.data.message);
      fetchData();
    } catch (error) {
      console.error('Delete invalid error:', error);
      alert('❌ Gagal menghapus data invalid');
    } finally {
      setLoading(false);
    }
  };

  // Checkbox handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleCopySelected = () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin dicopy terlebih dahulu');
      return;
    }

    const selectedData = data.filter(item => selectedIds.includes(item.id));
    
    // Format: ID | Nomor | Nama Bank | Nomor Rekening
	const copyText = selectedData.map(item =>
	  `${item.user_id || '-'}\t${item.nomor}\t${item.nama_bank}\t${item.nomor_rekening}\t${item.nama || '-'}`
	).join('\n');

	const header = 'ID\tNomor\tNama Bank\tNomor Rekening\tNama\n';
    const fullText = header + copyText;

    // Copy to clipboard
    navigator.clipboard.writeText(fullText).then(() => {
      alert(`✅ Berhasil copy ${selectedIds.length} data!\n\nData sudah ada di clipboard, paste di Excel/Spreadsheet.`);
    }).catch(err => {
      console.error('Copy failed:', err);
      alert('❌ Gagal copy data');
    });
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin dihapus terlebih dahulu');
      return;
    }

    if (!window.confirm(`⚠️ PERHATIAN!\n\nYakin ingin menghapus ${selectedIds.length} data terpilih?\nTindakan ini tidak dapat dibatalkan!`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Delete each selected item
      for (const id of selectedIds) {
        try {
          await axios.delete(`${API_URL}/upload/${id}`);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete ${id}:`, error);
          failCount++;
        }
      }

      alert(`✅ Selesai!\n\n${successCount} data berhasil dihapus${failCount > 0 ? `\n${failCount} data gagal dihapus` : ''}`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchData();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('❌ Gagal menghapus data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkMarkBlast = async () => {
    if (selectedIds.length === 0) {
      alert('Pilih data yang ingin di-blast terlebih dahulu');
      return;
    }

    if (!window.confirm(`Tandai ${selectedIds.length} data terpilih sudah di-blast?\n\nData akan dipindahkan ke menu Tracking dan counter Dashboard akan bertambah.`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Mark each selected item
      for (const id of selectedIds) {
        try {
          await axios.post(`${API_URL}/upload/mark-blast/${id}`);
          successCount++;
        } catch (error) {
          console.error(`Failed to mark blast ${id}:`, error);
          failCount++;
        }
      }

      alert(`✅ Selesai!\n\n${successCount} data berhasil di-blast${failCount > 0 ? `\n${failCount} data gagal (mungkin status bukan valid)` : ''}\n\nSilahkan cek:\n- Dashboard: Counter bertambah\n- Tracking: Data sudah masuk`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchData();
    } catch (error) {
      console.error('Bulk mark blast error:', error);
      alert('❌ Gagal mark blast data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteAll = async () => {
    if (!window.confirm('⚠️⚠️⚠️ PERINGATAN SERIUS! ⚠️⚠️⚠️\n\nAnda akan menghapus SEMUA data di menu Upload Data!\n\nTindakan ini:\n✗ TIDAK DAPAT dibatalkan\n✗ Akan menghapus SEMUA data (valid, invalid, unchecked)\n✗ Data yang sudah di-blast (di menu Tracking) TIDAK akan terhapus\n\nKetik "DELETE ALL" di prompt berikutnya jika yakin.')) {
      return;
    }

    const confirmText = prompt('Ketik "DELETE ALL" untuk konfirmasi (huruf besar semua):');
    
    if (confirmText !== 'DELETE ALL') {
      alert('❌ Konfirmasi dibatalkan. Penghapusan tidak dilakukan.');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/upload/delete-all`);

      if (response.data.success) {
        alert(`✅ Berhasil!\n\n${response.data.deletedCount} data telah dihapus permanen.`);
        fetchData();
      }
    } catch (error) {
      console.error('Delete all error:', error);
      alert('❌ Gagal menghapus data: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      valid: <span className="badge badge-success">Valid</span>,
      invalid: <span className="badge badge-danger">Invalid</span>,
      unchecked: <span className="badge badge-warning">Unchecked</span>,
      valid_by_rek: <span className="badge badge-success" style={{ background: '#059669' }}>Valid by Rek</span>,
      invalid_by_rek: <span className="badge badge-danger" style={{ background: '#dc2626' }}>Invalid by Rek</span>,
      'valid,valid_by_rek': (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <span className="badge badge-success">Valid</span>
          <span className="badge badge-success" style={{ background: '#059669' }}>Valid Rek</span>
        </span>
      ),
      'valid,invalid_by_rek': (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <span className="badge badge-success">Valid</span>
          <span className="badge badge-danger" style={{ background: '#dc2626' }}>Invalid Rek</span>
        </span>
      ),
      'invalid,invalid_by_rek': (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <span className="badge badge-danger">Invalid</span>
          <span className="badge badge-danger" style={{ background: '#dc2626' }}>Invalid Rek</span>
        </span>
      ),
      'invalid,valid_by_rek': (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <span className="badge badge-danger">Invalid</span>
          <span className="badge badge-success" style={{ background: '#059669' }}>Valid Rek</span>
        </span>
      )
    };
    return badges[status] || <span className="badge">{status}</span>;
  };

  return (
    <div className="upload-page">
      <div className="page-header">
        <h1>Upload & Manage Data</h1>
      </div>

      <div className="upload-section">
        <div className="upload-box">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            id="file-upload"
          />
          <label htmlFor="file-upload" className="file-label">
            <UploadIcon size={24} />
            <span>{file ? file.name : 'Pilih File Excel'}</span>
          </label>
          <button onClick={handleUpload} disabled={!file || loading} className="btn btn-primary">
            Upload
          </button>
        </div>
        <p style={{ marginTop: '10px', color: '#6b7280', fontSize: '14px' }}>
          📌 Format: id, nomor, nama_bank, nomor_rekening, nama (opsional) | Nomor akan dinormalisasi otomatis (prefix 62)
        </p>
      </div>

      <div className="filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="select">
          <option value="">Semua Status</option>
          <option value="valid">Valid</option>
          <option value="invalid">Invalid</option>
          <option value="unchecked">Unchecked</option>
          <option value="valid_by_rek">Valid by Rek</option>
          <option value="invalid_by_rek">Invalid by Rek</option>
        </select>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="select">
          <option value="created_at">Tanggal Upload</option>
          <option value="status">Status</option>
          <option value="nama">Nama</option>
        </select>

        <select value={order} onChange={(e) => setOrder(e.target.value)} className="select">
          <option value="DESC">Terbaru</option>
          <option value="ASC">Terlama</option>
        </select>

       <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {selectedIds.length > 0 && (
			  <>
				<button onClick={handleCopySelected} className="btn btn-primary">
				  <Copy size={18} />
				  Copy ({selectedIds.length})
				</button>
				<button onClick={handleBulkDelete} className="btn btn-danger" disabled={loading}>
				  <Trash2 size={18} />
				  Delete ({selectedIds.length})
				</button>
				<button onClick={handleBulkMarkBlast} className="btn btn-success" disabled={loading}>
				  <Send size={18} />
				  Blast ({selectedIds.length})
				</button>
				<button onClick={handleClearSelection} className="btn btn-secondary">
				  <XCircle size={18} />
				  Clear
				</button>
			  </>
			)}
          <button onClick={handleCheckAll} disabled={loading} className="btn btn-secondary">
            <RefreshCw size={18} />
            Cek Semua
          </button>
          <button onClick={handleDownloadValid} disabled={loading} className="btn btn-success">
            <Download size={18} />
            Download Valid
          </button>
          <button onClick={handleDeleteInvalid} disabled={loading} className="btn btn-danger">
            <AlertTriangle size={18} />
            Delete Invalid
          </button>
          <button onClick={handleDeleteAll} disabled={loading} className="btn" style={{ background: '#7f1d1d', color: 'white' }}>
            <Trash2 size={18} />
            Delete All Data
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading...</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
              </th>
			  <th>ID</th>
			  <th>Nomor</th>
			  <th>Nama Bank</th>
			  <th>Nomor Rekening</th>
			  <th>Nama</th>
			  <th>Tanggal Upload</th>
			  <th>Status</th>
			  <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleSelectOne(item.id)}
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                  />
                </td>
                <td>
                  {editingId === item.id ? (
                    <input
                      value={editData.user_id || ''}
                      onChange={(e) => setEditData({...editData, user_id: e.target.value})}
                      className="input-edit"
                      placeholder="ID"
                    />
                  ) : (item.user_id || '-')}
                </td>
                <td>
				  {editingId === item.id ? (
					<input
					  value={editData.nomor}
					  onChange={(e) => setEditData({...editData, nomor: e.target.value})}
					  className="input-edit"
					/>
				  ) : item.nomor}
				</td>
				<td>
				  {editingId === item.id ? (
                    <input
                      value={editData.nama_bank}
                      onChange={(e) => setEditData({...editData, nama_bank: e.target.value})}
                      className="input-edit"
                    />
                  ) : item.nama_bank}
                </td>
                <td>
				  {editingId === item.id ? (
					<input
					  value={editData.nomor_rekening}
					  onChange={(e) => setEditData({...editData, nomor_rekening: e.target.value})}
					  className="input-edit"
					/>
				  ) : item.nomor_rekening}
				</td>
				<td>
				  {editingId === item.id ? (
					<input
					  value={editData.nama || ''}
					  onChange={(e) => setEditData({...editData, nama: e.target.value})}
					  className="input-edit"
					  placeholder="Nama (opsional)"
					/>
				  ) : (item.nama || '-')}
				</td>
				<td style={{ fontSize: '13px', color: '#6b7280' }}>
				  {new Date(item.created_at).toLocaleDateString('id-ID', {
					year: 'numeric',
					month: 'short',
					day: 'numeric',
					hour: '2-digit',
					minute: '2-digit'
				  })}
				</td>
				<td>{getStatusBadge(item.status)}</td>
                <td>
                  <div className="action-buttons">
                    {editingId === item.id ? (
                      <>
                        <button onClick={handleSaveEdit} className="btn-icon btn-success">✓</button>
                        <button onClick={() => setEditingId(null)} className="btn-icon btn-secondary">✗</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleEdit(item)} className="btn-icon" title="Edit">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="btn-icon btn-danger" title="Delete">
                          <Trash2 size={16} />
                        </button>
                        <button
                          onClick={() => handleMarkBlast(item.id)}
                          className="btn-icon btn-primary"
                          title="Mark for Blast"
                        >
                          <Send size={16} />
                        </button>
                        {item.status === 'valid' && (
                          <button
                            onClick={() => setDepositModal({ show: true, id: item.id, nominal: '' })}
                            className="btn-icon btn-success"
                            title="Deposit"
                          >
                            <DollarSign size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <span>Halaman {page} dari {totalPages}</span>
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          className="btn btn-secondary"
        >
          Next
        </button>
      </div>

      {depositModal.show && (
        <div className="modal">
          <div className="modal-content">
            <h2>Input Nominal Deposit</h2>
            <input
              type="number"
              value={depositModal.nominal}
              onChange={(e) => setDepositModal({...depositModal, nominal: e.target.value})}
              placeholder="Masukkan nominal"
              className="input"
            />
            <div className="modal-buttons">
              <button onClick={handleDeposit} className="btn btn-primary">Simpan</button>
              <button onClick={() => setDepositModal({ show: false, id: null, nominal: '' })} className="btn btn-secondary">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Upload;